import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import moment from 'moment';
import { auth, getJWTToken, UpdateUserEmail } from '../../../Services/FirebaseService';
import ApiService from '../../../Services/ApiService';
import Client from '../../../Objects/models/Client';
import Child from '../../../Objects/models/Child';
import ClientRepository from '../../../Repositories/ClientRepository';
import ChildRepository from '../../../Repositories/ChildRepository';
import SponsorshipRepository from '../../../Repositories/SponsorshipRepository';
import { useBrandAuthConfig } from '../../../Hooks/Auth/useBrandAuthConfig';
import LayoutSteps from '../LayoutSteps';
import FormInput from '../../../Components/Ui/FormInput';
import FormCheckbox from '../../../Components/FormFields/FormCheckbox';
import IdentityFields from './IdentityFields';
import ReferralFields from './ReferralFields';
import useTailwindBrand from '../../../Theme/useTailwindBrand';
import validateEmail from '../../../Validation/Email';
import { userClientSetAccountAction, userUnSetCurrentRegister } from '../../../Redux/Actions/UserActions';
import { SHOW_ERROR, SHOW_SUCCESS } from '../../../Redux/Reducers/MessageReducers';
import { getActiveAppBrandConfig, getActiveBrandConfig, getActiveClientBrandConfig, getActivePrestataireBrandConfig, getActivePrestationBrandConfig } from '../../../config/environment.config';
import Functions from '../../../Helpers/Functions';

/**
 * Niveau par défaut de l'élève créé à l'inscription ("Vie active",
 * ClassEnum::OTHER côté backend) : le vrai niveau pourra être précisé dans
 * les paramètres.
 */
const DEFAULT_CHILD_CLASS = -6;

/**
 * Espace client : étape 2 de l'inscription commune (/register?=client).
 * L'identité (prénom, nom, genre, date de naissance, téléphone) est portée
 * par le bloc commun IdentityFields, partagé avec l'inscription prof. En
 * dessous, les champs propres au client : prénom de l'élève s'il est
 * différent, notification à chaque déclaration (isNotify) et acceptation des
 * offres promotionnelles (acceptMarketing).
 * Le profil est créé via POST /clients, déjà ouvert côté backend.
 */
function ClientRegisterSecond() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [user] = useAuthState(auth);
    const { designConfig } = useBrandAuthConfig();
    const { classes } = useTailwindBrand();

    const [loading, setLoading] = useState(false);
    // brouillon d'inscription (étape 1) : le téléphone saisi avec le mail y vit
    const clientDraft = useSelector((state: any) => state?.user?.currentUser?.client);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        studentFirstName: '',
        sex: '',
        birthday: '',
        phone: '',
        email: '',
        recommandedBy: '',
        sponsorship: '',
        isNotify: true,
        acceptMarketing: false,
    });
    const [option, setOption] = useState<number | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // l'élève est une autre personne que le titulaire du compte
    const studentDiffers = form.studentFirstName.trim().length > 1
        && form.studentFirstName.trim().toLowerCase() !== form.firstName.trim().toLowerCase();

    // même logique que côté prof : le téléphone n'est demandé que s'il est
    // inconnu (connexion Google, ou reprise d'une inscription abandonnée où
    // le brouillon de l'étape 1 est perdu), l'e-mail après une connexion par
    // téléphone
    const showPhone = !user?.phoneNumber
        && (user?.providerData[0]?.providerId === 'google.com' || !clientDraft?.phone);
    const showEmail = !!user?.phoneNumber;

    const handleChange = (key: keyof typeof form, value: string | boolean) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const validate = async () => {
        const nextErrors: Record<string, string> = {};
        if (form.firstName.trim().length < 2) nextErrors.firstName = 'Veuillez renseigner votre prénom.';
        if (form.lastName.trim().length < 2) nextErrors.lastName = 'Veuillez renseigner votre nom.';
        if (!form.sex) nextErrors.gender = 'Veuillez sélectionner votre genre.';
        if (!form.birthday) nextErrors.birthday = 'Veuillez renseigner votre date de naissance.';
        if (showPhone && form.phone.trim().length < 9) nextErrors.phone = 'Veuillez saisir un numéro de téléphone valide.';
        if (showEmail && !validateEmail(form.email).isValid) nextErrors.email = validateEmail(form.email).message;
        // le code de parrainage est optionnel côté client, mais vérifié s'il est saisi
        if (form.sponsorship.trim()) {
            try {
                const res = await SponsorshipRepository.retriveSponsorshipByCode(form.sponsorship.trim());
                if (!res) nextErrors.sponsorship = 'Code de parrainage invalide';
            } catch (err: any) {
                nextErrors.sponsorship = err?.data?.error ?? 'Code de parrainage invalide';
            }
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!(await validate()) || !user) return;
        setLoading(true);
        try {
            // connexion par téléphone : l'e-mail saisi devient celui du compte
            if (user.providerData[0]?.providerId === 'phone' && form.email) {
                await UpdateUserEmail(form.email);
            }
            const jwtToken = await getJWTToken(user);

            const client = new Client(
                jwtToken,
                form.firstName.trim(),
                form.lastName.trim(),
                // Google : téléphone saisi ici ; mail : celui de l'étape 1 ; SMS : celui du compte
                Functions.formatPhoneNumber(form.phone || clientDraft?.phone || user.phoneNumber || ''),
                form.acceptMarketing,
                'firebaseMessagingToken',
                studentDiffers,
                false
            );
            client.mail = user.email ?? form.email ?? '';
            client.sex = form.sex;
            client.birthday = moment(form.birthday).toDate();
            client.isNotify = form.isNotify;
            // parrainage optionnel, même logique de stockage que côté prof
            const recommandedBy = form.recommandedBy.trim().toLowerCase()
                || (option ? Functions.recommandedByDatas[option - 1] : '');
            if (recommandedBy) {
                client.recommandedBy = recommandedBy;
            }
            client.sponsorship = form.sponsorship.trim() ? form.sponsorship.trim().toUpperCase() : null;

            // POST /clients est ouvert aux anonymes : un header Authorization
            // résiduel (utilisateur pas encore en base) provoquerait un 403
            ApiService.disableAuth();
            const created = await ClientRepository.createClient(client);
            ApiService.enableAuth(undefined, undefined, `client ${jwtToken}`);
            dispatch(userClientSetAccountAction(JSON.parse(JSON.stringify(created))));

            // prénom de l'élève différent : on crée l'élève rattaché avec la
            // date de naissance du titulaire et un niveau par défaut ("Vie
            // active", ClassEnum OTHER), à affiner ensuite dans les
            // paramètres ; l'inscription n'est jamais bloquée en cas d'échec
            if (studentDiffers && created['@id']) {
                try {
                    const child = new Child(
                        form.studentFirstName.trim(),
                        form.lastName.trim(),
                        created['@id']!,
                        DEFAULT_CHILD_CLASS,
                        moment(form.birthday).toDate(),
                        '',
                        ''
                    );
                    await ChildRepository.createChild(child);
                } catch (childError) {
                    // l'élève pourra être complété dans les paramètres
                }
            }

            // dispatch({ type: SHOW_SUCCESS, payload: `Bienvenue chez ${getActiveAppBrandConfig()} ! Votre compte est prêt.` });
            // le client créé (avec id) est en Redux : la page d'inscription
            // affiche l'étape 3 (félicitations), qui mène au tableau de bord
        } catch (requestError: any) {
            // profil déjà en base (double clic, retour arrière) : on entre
            try {
                const jwtToken = await getJWTToken(user);
                ApiService.enableAuth(undefined, undefined, `client ${jwtToken}`);
                const existing = await ClientRepository.getCurrentClient();
                if (existing) {
                    dispatch(userClientSetAccountAction(JSON.parse(JSON.stringify(existing))));
                    dispatch(userUnSetCurrentRegister());
                    navigate(`/${getActiveBrandConfig()}/tableau-de-bord`);
                    return;
                }
            } catch (fallbackError) {
                // rien : on affiche l'erreur ci-dessous
            }
            dispatch({ type: SHOW_ERROR, payload: 'La création de votre compte a échoué. Veuillez réessayer.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <LayoutSteps
            designConfig={designConfig}
            loading={loading}
            handleSubmit={handleSubmit}
            btnText="Finaliser mon inscription"
            stepNumber={2}
            isClient
            totalSteps={3}
            children={
                <div className="w-full grid lg:grid-cols-2 grid-cols-1 gap-4" key={2}>
                    {/* Identité commune prof / client */}
                    <IdentityFields
                        values={{
                            firstName: form.firstName,
                            lastName: form.lastName,
                            gender: form.sex,
                            phone: form.phone,
                            email: form.email,
                        }}
                        errors={errors}
                        onFieldChange={(name, value) => handleChange(name as 'firstName' | 'lastName' | 'phone' | 'email', value)}
                        onGenderChange={(gender) => handleChange('sex', gender)}
                        onBirthdayChange={(newDate: any) => handleChange('birthday', newDate ? moment(newDate.toDate ? newDate.toDate() : newDate).format('YYYY-MM-DD') : '')}
                        birthdayDefault={form.birthday}
                        showPhone={showPhone}
                        showEmail={showEmail}
                    />
                    {/* Champs propres au client */}
                    <FormInput
                        label={`Prénom de ${getActiveClientBrandConfig("", "l'", 'votre ')} (si différent)`}
                        name="studentFirstName"
                        placeholder="Laissez vide si c'est vous"
                        value={form.studentFirstName}
                        onChange={(e) => handleChange('studentFirstName', e.target.value)}
                    />
                    {/* Parrainage : mêmes écrans que côté prof, tout optionnel */}
                    <ReferralFields
                        option={option}
                        onOptionChange={setOption}
                        recommandedBy={form.recommandedBy}
                        onRecommandedByChange={(value) => handleChange('recommandedBy', value)}
                        sponsorship={form.sponsorship}
                        onSponsorshipChange={(value) => handleChange('sponsorship', value)}
                        sponsorshipError={errors.sponsorship}
                        codeRequired={false}
                    />
                    <div className="lg:col-span-2 space-y-2">
                        <FormCheckbox
                            label={`Être notifié à chaque fois que mon ${getActivePrestataireBrandConfig()} déclare ${getActivePrestationBrandConfig('', 'un ', 'une ')} pour moi.`}
                            classLabel={`${classes.text} font-bold`}
                            checked={form.isNotify}
                            required={false}
                            onChange={() => handleChange('isNotify', !form.isNotify)}
                        />
                        <FormCheckbox
                            label={`J'accepte de recevoir des offres promotionnelles de la part de ${getActiveAppBrandConfig()}.`}
                            classLabel={`${classes.text} font-bold`}
                            checked={form.acceptMarketing}
                            required={false}
                            onChange={() => handleChange('acceptMarketing', !form.acceptMarketing)}
                        />
                    </div>
                </div>
            }
        />
    );
}

export default ClientRegisterSecond;
